import { computed } from "mobx";
import { watchBusy } from "@frui.ts/screens";
import { bound } from "@frui.ts/helpers";
import { Modules } from "config";
import type { interfaces } from "inversify";
import EventBus, { deadLetterTopicMessagesChanged } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import type DeadLetterTopicMessageDetailResponse from "entities/deadLetterTopicMessageDetailResponse";
import type kafkaDltMessageType from "entities/kafkaDltMessageType";
import type kafkaDltTopic from "entities/kafkaDltTopic";
import ProcessingMethod from "entities/processingMethod";
import EditableDetailViewModel from "viewModels/editableDetailViewModel";
import AlertsViewModel from "viewModels/alertsViewModel";
import DeadLetterTopicsRepository from "repositories/deadLetterTopicsRepository";
import type PagingManagerViewModel from "viewModels/pagingManagerViewModel";
import type { IWithPrevNextNavigationViewModel } from "viewModels/types";

export default class DeadLetterTopicsSearchDetailViewModel
  extends EditableDetailViewModel<DeadLetterTopicMessageDetailResponse>
  implements IWithPrevNextNavigationViewModel
{
  readonly modelName = "deadLetterTopicsSearch";

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get hasNext() {
    return this.pagingManager.hasNext;
  }

  @computed
  get hasPrevious() {
    return this.pagingManager.hasPrevious;
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("system.dlt", "write");
  }

  @bound
  @watchBusy
  async onNextClick() {
    const nextItemId = await this.pagingManager.onNextClick();

    if (nextItemId) {
      this.itemId = nextItemId;
      this.navigationName = `${nextItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const prevItemId = await this.pagingManager.onPreviousClick();

    if (prevItemId) {
      this.itemId = prevItemId;
      this.navigationName = `${prevItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  constructor(
    protected itemId: string,
    private pagingManager: PagingManagerViewModel,
    protected repository: DeadLetterTopicsRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(itemId);
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
  }

  protected async loadDetail() {
    return await this.repository.get(this.itemId);
  }

  @bound
  async onResubmissionClick() {
    try {
      await this.repository.resubmissionAll({
        dltMessageIds: [this.itemId],
        resubmissionMethod: ProcessingMethod.ProcessSelected,
      });
      this.eventBus.publish(deadLetterTopicMessagesChanged());
      this.notificationService.add({ title: this.translate("dltResubmission"), type: NotificationType.SUCCESS });
      this.requestClose();
    } catch (e) {
      // Silent
    }
  }

  @bound
  async onDiscardClick() {
    try {
      await this.repository.discardAll({ dltMessageIds: [this.itemId], discardMethod: ProcessingMethod.ProcessSelected });
      this.eventBus.publish(deadLetterTopicMessagesChanged());
      this.requestClose();
    } catch (e) {
      // Silent
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `deadLetterTopicsDetail.${code}`, size);
  }

  translateTypeState(state: kafkaDltMessageType) {
    return this.localizationService.translateEnum("kafkaDltMessageType", state);
  }

  translateTopicState(state: kafkaDltTopic) {
    return this.localizationService.translateEnum("kafkaDltTopic", state);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, pagingManager: PagingManagerViewModel<any>) =>
      new DeadLetterTopicsSearchDetailViewModel(
        itemId,
        pagingManager,
        container.get(DeadLetterTopicsRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(AlertsViewModel)
      );
  }

  updateData(): Promise<DeadLetterTopicMessageDetailResponse> {
    return this.repository.update(this.item);
  }
}
